import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../lib/api';
import { SERVICE_OPTIONS, DEFAULT_PAYMENT_TERMS_DAYS } from '../lib/services';

const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const PaymentModal = ({ invoiceId, balanceDue, onClose, onSaved }) => {
  const [invoices, setInvoices] = useState([]);
  const [banks, setBanks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoiceMeta, setInvoiceMeta] = useState(null);
  // Payment source: 'existing' (pick from dropdown) | 'new' (create inline)
  const [source, setSource] = useState('existing');
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    invoice_id: invoiceId || '',
    amount: balanceDue || '',
    date: today,
    bank_account_id: '',
    mode: 'NEFT',
    payment_type: 'Retainer',
    tds_percent: '',
    reference: '',
    notes: '',
  });
  const [newInv, setNewInv] = useState({
    company_id: '',
    client_id: '',
    invoice_no: '',
    date: today,
    due_date: addDays(today, DEFAULT_PAYMENT_TERMS_DAYS),
    service: 'Marketing',
    amount: '',
    gst_percent: 18,
    tds_percent: 0,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!invoiceId) {
      api.get('/invoices').then(res => setInvoices(res.data.filter(i => i.status !== 'paid')));
      api.get('/companies').then(res => setCompanies(res.data));
      api.get('/clients').then(res => setClients(res.data));
    }
    api.get('/bank-accounts').then(res => setBanks(res.data));
  }, [invoiceId]);

  // Pull subtotal when invoice changes so TDS% can auto-calc amount
  useEffect(() => {
    const iid = form.invoice_id;
    if (!iid) { setInvoiceMeta(null); return; }
    api.get(`/invoices/${iid}`).then(res => {
      const inv = res.data;
      const subtotal = (inv.items || []).reduce((s, i) => s + (parseFloat(i.amt) || 0), 0);
      setInvoiceMeta({ subtotal, gst_percent: parseFloat(inv.gst_percent) || 0, tds_percent: parseFloat(inv.tds_percent) || 0 });
    }).catch(() => setInvoiceMeta(null));
  }, [form.invoice_id]);

  const isTds = form.mode === 'TDS';

  useEffect(() => {
    if (isTds && invoiceMeta && form.tds_percent) {
      const pct = parseFloat(form.tds_percent) || 0;
      const amt = (invoiceMeta.subtotal * pct / 100).toFixed(2);
      setForm(f => ({ ...f, amount: amt }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.tds_percent, isTds, invoiceMeta]);

  // Auto-fetch next invoice # when company changes in "new" mode
  useEffect(() => {
    if (source === 'new' && newInv.company_id) {
      api.get(`/invoices/next-number/${newInv.company_id}`).then(res => {
        setNewInv(n => ({ ...n, invoice_no: res.data.invoice_no }));
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, newInv.company_id]);

  const clientOptionsForCompany = clients.filter(c => !newInv.company_id || c.company_id === newInv.company_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let invoiceIdToUse = form.invoice_id;

      // Inline invoice creation
      if (source === 'new') {
        if (!newInv.company_id || !newInv.client_id || !newInv.invoice_no || !newInv.amount) {
          alert('Please fill company, client, invoice # and amount for the new invoice.');
          setSaving(false); return;
        }
        const amt = parseFloat(newInv.amount) || 0;
        const invPayload = {
          invoice_no: newInv.invoice_no,
          date: newInv.date,
          due_date: newInv.due_date,
          company_id: newInv.company_id,
          client_id: newInv.client_id,
          service: newInv.service || null,
          period: null,
          items: [{ desc: newInv.service || 'Service', qty: 1, rate: amt, amt }],
          gst_percent: parseFloat(newInv.gst_percent) || 0,
          tds_percent: parseFloat(newInv.tds_percent) || 0,
          notes: newInv.notes || null,
          status: 'pending', // will become paid/partial once payment is added
          source: 'generated',
        };
        const invRes = await api.post('/invoices', invPayload);
        invoiceIdToUse = invRes.data.id;
      }

      const payload = {
        invoice_id: invoiceIdToUse,
        amount: parseFloat(form.amount),
        date: form.date,
        mode: form.mode,
        payment_type: form.payment_type || null,
        reference: form.reference || null,
        notes: form.notes || null,
      };
      if (isTds) {
        payload.tds_percent = form.tds_percent ? parseFloat(form.tds_percent) : null;
        payload.bank_account_id = null;
      } else {
        payload.bank_account_id = form.bank_account_id;
      }
      await api.post('/payments', payload);
      onSaved();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors";
  const labelCls = "block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div data-testid="payment-modal" className="bg-cream border border-border max-w-lg w-full p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading text-2xl font-light">Record Payment</h2>
          <button data-testid="close-modal-button" onClick={onClose} className="text-text-secondary hover:text-maroon"><X className="w-5 h-5" strokeWidth={1} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!invoiceId && (
            <>
              {/* Source toggle */}
              <div className="flex gap-2 p-1 bg-surface border border-border">
                <button type="button" data-testid="pay-source-existing" onClick={() => setSource('existing')} className={`flex-1 py-2 font-body text-xs uppercase tracking-widest transition-colors ${source === 'existing' ? 'bg-maroon text-white' : 'text-text-secondary hover:text-maroon'}`}>Existing Invoice</button>
                <button type="button" data-testid="pay-source-new" onClick={() => setSource('new')} className={`flex-1 py-2 font-body text-xs uppercase tracking-widest transition-colors ${source === 'new' ? 'bg-maroon text-white' : 'text-text-secondary hover:text-maroon'}`}>New Invoice + Payment</button>
              </div>

              {source === 'existing' ? (
                <div>
                  <label className={labelCls}>Invoice</label>
                  <select data-testid="payment-invoice" required value={form.invoice_id} onChange={(e) => setForm({ ...form, invoice_id: e.target.value })} className={inputCls}>
                    <option value="">Select invoice</option>
                    {invoices.map(i => <option key={i.id} value={i.id}>{i.invoice_no} - {i.client_name}</option>)}
                  </select>
                </div>
              ) : (
                <div className="border border-border bg-surface p-4 space-y-3">
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon">New Invoice Details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Company</label>
                      <select data-testid="new-inv-company" required value={newInv.company_id} onChange={(e) => setNewInv({ ...newInv, company_id: e.target.value, client_id: '' })} className={inputCls}>
                        <option value="">Select</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Client</label>
                      <select data-testid="new-inv-client" required value={newInv.client_id} onChange={(e) => setNewInv({ ...newInv, client_id: e.target.value })} className={inputCls} disabled={!newInv.company_id}>
                        <option value="">Select</option>
                        {clientOptionsForCompany.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className={labelCls}>Invoice #</label>
                      <input data-testid="new-inv-no" required value={newInv.invoice_no} onChange={(e) => setNewInv({ ...newInv, invoice_no: e.target.value })} className={inputCls} placeholder="Auto-suggested — edit if needed" />
                    </div>
                    <div>
                      <label className={labelCls}>Date</label>
                      <input required type="date" value={newInv.date} onChange={(e) => setNewInv({ ...newInv, date: e.target.value, due_date: addDays(e.target.value, DEFAULT_PAYMENT_TERMS_DAYS) })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Due Date</label>
                      <input required type="date" value={newInv.due_date} onChange={(e) => setNewInv({ ...newInv, due_date: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Service</label>
                      <select value={newInv.service} onChange={(e) => setNewInv({ ...newInv, service: e.target.value })} className={inputCls}>
                        {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Amount (subtotal ₹)</label>
                      <input data-testid="new-inv-amount" required type="number" step="0.01" value={newInv.amount} onChange={(e) => setNewInv({ ...newInv, amount: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>GST %</label>
                      <input type="number" value={newInv.gst_percent} onChange={(e) => setNewInv({ ...newInv, gst_percent: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>TDS %</label>
                      <input type="number" value={newInv.tds_percent} onChange={(e) => setNewInv({ ...newInv, tds_percent: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                  <p className="font-body text-xs text-text-secondary">The invoice will be created first, then this payment will be applied to it.</p>
                </div>
              )}
            </>
          )}

          <div>
            <label className={labelCls}>Type</label>
            <select data-testid="payment-type" required value={form.payment_type} onChange={(e) => setForm({ ...form, payment_type: e.target.value })} className={inputCls}>
              {['AD Spend', 'Retainer', 'One-time', 'Subscription', 'TDS'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className={labelCls}>Mode</label>
            <select data-testid="payment-mode" required value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={inputCls}>
              {['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque', 'Cash', 'TDS'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {isTds && (
            <div>
              <label className={labelCls}>TDS %</label>
              <input
                data-testid="payment-tds-percent"
                type="number"
                step="0.01"
                value={form.tds_percent}
                onChange={(e) => setForm({ ...form, tds_percent: e.target.value })}
                className={inputCls}
                placeholder="e.g. 10 or 2"
              />
              {invoiceMeta && form.tds_percent && (
                <p className="font-body text-xs text-text-secondary mt-1">
                  {form.tds_percent}% of subtotal ₹{invoiceMeta.subtotal.toLocaleString('en-IN')} = auto-filled amount.
                </p>
              )}
            </div>
          )}

          <div>
            <label className={labelCls}>Amount (₹)</label>
            <input data-testid="payment-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>Date</label>
            <input data-testid="payment-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
          </div>

          {!isTds && (
            <div>
              <label className={labelCls}>Bank Account</label>
              <select data-testid="payment-bank" required={!isTds} value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} className={inputCls}>
                <option value="">Select bank</option>
                {banks.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className={labelCls}>Reference {isTds && <span className="text-text-secondary normal-case lowercase tracking-normal">(e.g. TDS certificate no, 26AS section)</span>}</label>
            <input data-testid="payment-reference" type="text" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className={inputCls} />
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-5 py-2 font-heading text-base transition-colors">Cancel</button>
            <button data-testid="save-payment-button" type="submit" disabled={saving} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-base transition-colors disabled:opacity-50">{saving ? 'Saving...' : 'Save Payment'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;
